import { coreApi, stockApi } from '$api'
import { ISupplier, Supplier } from '$models'
import { getActiveCompanySettings, singletonQueryClient } from '$utils'
import { SupplierPatchRequest } from '@loadedhub/loaded-stock-api-sdk'

enum QueryKeys {
  getAllVerifiedSuppliers = 'getAllVerifiedSuppliers',
  getAllSuppliers = 'getAllSuppliers',
}

/**
 * Retrieves all verified suppliers from auth server
 * @returns an list of verified suppliers
 */
export const getVerifiedSuppliers = async (): Promise<ISupplier[]> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return []

  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.getAllVerifiedSuppliers],
    queryFn: async () => {
      return await coreApi.getVerifiedSuppliers()
    },
  })
}

/**
 * Retrieves all suppliers
 * @returns an list of suppliers
 */
export const getAllSuppliers = async (
  includeDeleted?: boolean
): Promise<ISupplier[]> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return []

  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.getAllSuppliers, includeDeleted],
    queryFn: async ({ signal }) => {
      return await stockApi.getSuppliers(includeDeleted, signal)
    },
  })
}

/**
 * Creates a new supplier
 * @param newSupplier the new supplier to be created
 * @returns the created supplier
 */
export const createSupplier = async (
  newSupplier: ISupplier
): Promise<ISupplier> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) {
    throw new Error(
      "The user doesn't have permission to create a new supplier."
    )
  }

  return await stockApi.createSupplier(new Supplier(newSupplier))
}

export const updateSupplierThirdPartyContactId = async (
  supplierId: string,
  thirdPartyContactId: string | undefined
): Promise<ISupplier | null> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return null

  return await stockApi.updateSupplierThirdPartyContactId(
    supplierId,
    new SupplierPatchRequest({ thirdPartyContactId })
  )
}
